import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { 
  Activity, 
  Users, 
  Calendar, 
  BarChart3, 
  Settings, 
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';

const Sidebar = ({ isOpen, onToggle }) => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 768) {
      onToggle();
    }
  };

  // All navigation items
  const allNavItems = [
    { to: '/calendar', icon: Calendar, label: 'Calendar', mobileVisible: true },
    { to: '/patients', icon: Users, label: 'Pacienți', mobileVisible: true },
    { to: '/reports', icon: BarChart3, label: 'Rapoarte', mobileVisible: false },
  ];

  if (user?.role === 'admin' || user?.role === 'medic') {
    allNavItems.push({ to: '/settings', icon: Settings, label: 'Setări', mobileVisible: false });
  }

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={onToggle}
        className={cn(
          "md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-slate-900 text-white shadow-lg",
          isOpen && "hidden"
        )}
        data-testid="mobile-menu-toggle"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "w-64 h-screen bg-slate-900 text-white flex flex-col fixed left-0 top-0 overflow-y-auto overflow-x-hidden z-50",
          "transition-transform duration-300 ease-in-out",
          // Mobile: hidden by default, slide in when open
          "md:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Close button for mobile */}
        <button
          onClick={onToggle}
          className="md:hidden absolute top-4 right-4 p-1 text-slate-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Logo */}
        <div className="p-4 md:p-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", theme.primaryBg)}>
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight font-[Manrope]">DiabetoCare</h1>
              <p className="text-xs text-slate-400">Cabinet Diabetologie</p>
            </div>
          </div>
        </div>

        <Separator className="bg-slate-800 flex-shrink-0" />

        {/* User section - MOVED UP for mobile visibility */}
        <div className="p-3 md:hidden flex-shrink-0">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-800">
            <div className={cn("w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0", theme.primaryBg)}>
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className={cn("text-xs capitalize", theme.primaryText)}>{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full mt-2 text-slate-300 hover:text-white hover:bg-slate-800 justify-start py-2.5"
            onClick={handleLogout}
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2 flex-shrink-0" />
            Deconectare
          </Button>
        </div>

        <Separator className="bg-slate-800 flex-shrink-0 md:hidden" />

        {/* Navigation */}
        <nav className="flex-1 p-3 md:p-4 space-y-1 min-h-0">
          {allNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-all duration-200',
                  isActive 
                    ? cn(theme.primaryBg, 'text-white')
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                  // Hide on mobile if not mobileVisible
                  !item.mobileVisible && 'hidden md:flex'
                )
              }
              data-testid={`nav-${item.to.replace('/', '')}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium text-sm md:text-base">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <Separator className="bg-slate-800 flex-shrink-0 hidden md:block" />

        {/* User section - Desktop only (at bottom) */}
        <div className="p-3 md:p-4 flex-shrink-0 hidden md:block">
          <div className="flex items-center gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg bg-slate-800">
            <div className={cn("w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0", theme.primaryBg)}>
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className={cn("text-xs capitalize", theme.primaryText)}>{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full mt-2 text-slate-300 hover:text-white hover:bg-slate-800 justify-start py-2.5"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2 flex-shrink-0" />
            Deconectare
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
